import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SettingsSectionCard } from "@/features/seller-portal/SettingsSectionCard";
import { PhoneChangeDialog } from "@/features/seller-portal/PhoneChangeDialog";
import { SELLER_PORTAL_NS } from "@/features/seller-portal/i18n";
import { useUpdateSellerProfile } from "@/features/seller-portal/hooks";
import type { MyShopProfile } from "@/features/seller-portal/api";

export function ContactChannelsSection({
  profile,
  readOnly,
}: {
  profile: MyShopProfile;
  readOnly: boolean;
}): ReactElement {
  const { t } = useTranslation(SELLER_PORTAL_NS);
  const update = useUpdateSellerProfile();
  const [whatsapp, setWhatsapp] = useState(profile.seller.whatsapp_phone_e164 ?? "");
  const [telegram, setTelegram] = useState(profile.seller.telegram_handle ?? "");
  const [dialogOpen, setDialogOpen] = useState(false);

  const onSave = (): void => {
    update.mutate(
      {
        whatsapp_phone_e164: whatsapp || undefined,
        telegram_handle: telegram || undefined,
      },
      {
        onSuccess: () => toast.success(t("settings.saved")),
        onError: () => toast.error(t("settings.saveError")),
      },
    );
  };

  return (
    <>
      <SettingsSectionCard
        title={t("settings.sections.contacts")}
        testid="settings-section-contacts"
        onSave={onSave}
        saving={update.isPending}
        disabled={readOnly}
      >
        <div>
          <Label htmlFor="set-phone">{t("settings.contacts.phone")}</Label>
          <Input id="set-phone" value={profile.seller.phone_e164} disabled readOnly />
          <button
            type="button"
            data-testid="open-phone-change"
            disabled={readOnly}
            onClick={() => setDialogOpen(true)}
            className="mt-1 text-xs font-semibold text-[var(--g-primary-ink)] disabled:opacity-50"
          >
            {t("settings.contacts.changePhone")}
          </button>
          <p className="text-xs text-[var(--g-ink-3)]">{t("settings.contacts.phoneHint")}</p>
        </div>

        <div>
          <Label htmlFor="set-whatsapp">{t("settings.contacts.whatsapp")}</Label>
          <Input
            id="set-whatsapp"
            data-testid="set-whatsapp"
            value={whatsapp}
            disabled={readOnly}
            onChange={(e) => setWhatsapp(e.target.value)}
            placeholder={t("settings.contacts.phonePlaceholder")}
          />
        </div>

        <div>
          <Label htmlFor="set-telegram">{t("settings.contacts.telegram")}</Label>
          <Input
            id="set-telegram"
            data-testid="set-telegram"
            value={telegram}
            disabled={readOnly}
            onChange={(e) => setTelegram(e.target.value)}
            placeholder={t("settings.contacts.telegramPlaceholder")}
          />
        </div>
      </SettingsSectionCard>

      {dialogOpen ? <PhoneChangeDialog onClose={() => setDialogOpen(false)} /> : null}
    </>
  );
}
